/**
 * BitFi read fragments — two unrelated surfaces under one brand.
 *
 * **bfBTC** ({@link BfbtcReadAbi}) is a LayerZero OFT that is ALSO its own
 * staking contract: there is no separate vault, so `deposit`, `requestWithdraw`,
 * `previewWithdraw` and the ratio all live on the token. It is not ERC-4626 in
 * any part — `asset()`, `totalAssets()` and `convertToAssets()` all revert.
 *
 * **hbfUSD / pbfUSD** ({@link BfusdVaultReadAbi}) ARE real ERC-4626 vaults over
 * bfUSD, so the generic 4626 fragments cover most of them; this file adds only
 * what the generic reader cannot see — the epoch ledger, the supply cap, the
 * separate DEPOSIT ratio and the redemption fee.
 *
 * Recovered from Sourcify `exact_match` sources (Ethereum `Bfbtc`
 * `0x4ca67a79…`, `StakedBitFiStablecoin` `0xd3222453…`), NOT from the docs: the
 * two ABI JSONs `developer/bfbtc-overview/contract-abi` links to are 404 on
 * both `main` and `master`.
 */

/**
 * bfBTC — the token, the staking contract and the price, all one address.
 *
 * **`currentRatio()` is INVERTED and 1e8-scaled**: it is bfBTC-per-BTC, so a
 * holder's rate is `1e16 / currentRatio` (1 bfBTC = 1.10915 BTC at 90159130).
 * It only ever falls. Never publish it as a share price.
 *
 * **`ratio(epoch)` is the historical series** and is what makes an APR readable
 * without an archival node, a log scan or an API — all three of which are
 * unavailable here (public Ethereum RPCs refuse the log window, and
 * `api-pub.bitfi.one` answers `Unauthorized` on every route). Two traps:
 * `ratio(currentEpoch)` is **0** — the live epoch is not written until it
 * settles, so the newest readable entry is `currentEpoch - 1` — and the epoch
 * INDEX is chain-local (417 on Ethereum, 444 on BNB, 76 on Pharos for the same
 * ratio), so an index may never be compared across chains. Only the VALUES are
 * global, and even then not on every chain: TAC and GOAT were 3.9 % behind the
 * rest when this was written, so each chain is read on its own.
 *
 * `getLatestPrice()` is NOT a USD price — it answers a hardcoded `1e6` on every
 * chain and `oracle()` is the zero address, i.e. the RedStone feed the docs
 * describe is not wired. bfBTC's USD value has to be derived from BTC.
 */
export const BfbtcReadAbi = [
  {
    name: 'currentRatio',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'currentEpoch',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'ratio',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'underlyingAsset',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
  {
    name: 'underlyingDecimals',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'cooldownEpoches',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'minDepositTokenAmount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'minWithdrawBfbtcAmount',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    // `feeType` 0 = withdraw to this EVM chain, 1 = withdraw to the Bitcoin
    // network. `percentageFee` is scaled by 100_000 and `fixedFee` by 1e8
    // (bfBTC's own decimals). Either leg is DISABLED by making its fee
    // unpayable rather than by a flag — see `BITFI_FEE_PRECISION`.
    name: 'feeConfigs',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint8' }],
    outputs: [
      { type: 'uint128', name: 'percentageFee' },
      { type: 'uint128', name: 'fixedFee' },
    ],
  },
  {
    name: 'previewWithdraw',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { type: 'uint256', name: 'share' },
      { type: 'bool', name: 'native' },
    ],
    outputs: [
      { type: 'uint256', name: 'underlyingAmount' },
      { type: 'uint256', name: 'fee' },
    ],
  },
  {
    name: 'previewDeposit',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'amount' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'paused',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'bool' }],
  },
  {
    name: 'isBlacklisted',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'bool' }],
  },
  {
    name: 'multisig',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
  {
    // The full withdrawal record. `status` walks PENDING → CLAIMABLE (EVM) or
    // SENT_ON_NATIVE (Bitcoin) → CLAIMED, and only the multisig moves it.
    name: 'withdrawals',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256' }],
    outputs: [
      { type: 'address', name: 'user' },
      { type: 'uint256', name: 'amount' },
      { type: 'bytes32', name: 'btcAddress' },
      { type: 'uint256', name: 'epoch' },
      { type: 'uint256', name: 'settleEpoch' },
      { type: 'bytes32', name: 'nativeTx' },
      { type: 'uint8', name: 'btcAddressType' },
      { type: 'uint8', name: 'status' },
    ],
  },
  {
    name: 'withdrawIdCounter',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'claimable',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'id' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

/**
 * bfBTC write surface — see the calldata layer for how each leg is gated.
 *
 * `deposit` pulls `underlyingAsset()` with `transferFrom` and mints to
 * `msg.sender`; there is NO receiver argument on any leg, in either direction.
 * `requestWithdraw` burns and books a record the multisig must approve before
 * `claim` pays out, so a withdrawal can never be completed by calldata alone.
 */
export const BfbtcWriteAbi = [
  {
    name: 'deposit',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { type: 'uint256', name: 'amount' },
      { type: 'uint256', name: 'minAmount' },
    ],
    outputs: [],
  },
  {
    // Bitlayer only: the underlying is the chain's native gas token.
    name: 'depositNativeGas',
    type: 'function',
    stateMutability: 'payable',
    inputs: [{ type: 'uint256', name: 'minAmount' }],
    outputs: [],
  },
  {
    name: 'requestWithdraw',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ type: 'uint256', name: 'amount' }],
    outputs: [],
  },
  {
    name: 'requestWithdrawNative',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { type: 'uint256', name: 'amount' },
      { type: 'uint8', name: 'btcAddressType' },
      { type: 'bytes32', name: 'btcAddress' },
    ],
    outputs: [],
  },
  {
    name: 'claim',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ type: 'uint256[]', name: 'withdrawIds' }],
    outputs: [],
  },
] as const

/**
 * hbfUSD / pbfUSD — ERC-4626 plus BitFi's epoch ledger.
 *
 * **`epochRatios(n)` carries its own clock**: `(startRatio, endRatio, startTime,
 * endTime)`, all on-chain, so the APR here is exact rather than inferred — no
 * block-timestamp arithmetic, which is the mistake `wren-nav` documents. The
 * share price VESTS linearly from `startRatio` to `endRatio` across the window,
 * and the window is 23 h with a settlement gap after it (measured: epochs start
 * ~89,580 s apart, so an epoch is NOT a day).
 *
 * **`currentDepositRatio()` is not `currentRatio()`** — it is the epoch's
 * `endRatio`, i.e. a depositor is priced at the END of the vesting window while
 * a redeemer gets the interpolated value. Buying in costs tomorrow's price, so
 * `previewDeposit` and `previewRedeem` disagree by more than rounding and
 * neither may be used for the other leg.
 */
export const BfusdVaultReadAbi = [
  {
    name: 'currentRatio',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'currentDepositRatio',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'epochRatios',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256' }],
    outputs: [
      { type: 'uint64', name: 'startRatio' },
      { type: 'uint64', name: 'endRatio' },
      { type: 'uint64', name: 'startTime' },
      { type: 'uint64', name: 'endTime' },
    ],
  },
  {
    name: 'settlementDelay',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    // Share-denominated, NOT underlying-denominated: compare it against
    // `totalSupply()`, and convert with the ratio before reporting a
    // deposit capacity in bfUSD.
    name: 'supplyCap',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'pendingWithdrawals',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'redemptionFee',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { type: 'uint128', name: 'percentageFee' },
      { type: 'uint128', name: 'fixedFee' },
    ],
  },
  {
    name: 'withdrawalIdCounter',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'withdrawals',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256' }],
    outputs: [
      { type: 'address', name: 'user' },
      { type: 'bool', name: 'claimed' },
      { type: 'uint256', name: 'amount' },
      { type: 'uint256', name: 'epoch' },
      { type: 'uint256', name: 'unvestedYield' },
    ],
  },
  {
    name: 'paused',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'bool' }],
  },
] as const

/** hbfUSD / pbfUSD write surface. Standard 4626 in, request-and-claim out. */
export const BfusdVaultWriteAbi = [
  {
    name: 'claimWithdrawals',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ type: 'uint256[]', name: 'withdrawalIds' }],
    outputs: [],
  },
] as const

/**
 * Denominator for every `percentageFee` on both BitFi surfaces.
 *
 * A fee of `BITFI_FEE_PRECISION` is 100 %, which is how BitFi DISABLES a
 * withdrawal route: `requestWithdraw` reverts when the fee reaches the amount,
 * so Base, Core, TAC, GOAT and Pharos — all of which publish
 * `feeConfigs(0).percentageFee == 100000` — have no EVM exit at all, only the
 * Bitcoin-network one. There is no boolean anywhere that says so.
 */
export const BITFI_FEE_PRECISION = 100_000n
